// ... Other imports
import React, { useState, useEffect, useRef } from "react";
import config from "../../config";
import "./AddBrochureOrder.css";
import {
  ParticipantInfo,
  CreateFundraiserParticipant,
  ParticipantCreationResponse,
} from "../../Interfaces/FundraiserRole";
import { PrimaryDivision, SecondaryDivision } from "../../Interfaces/Division";
import { Product } from "../../Interfaces/Product";
import { OrderToSubmit } from "../../Interfaces/Orders";
import fetchWithAuth from "../../services/fetchWithAuth";

interface AddBrochureOrderProps {
  fundraiserID: number;
  brochureOnlyAccountID: number;
  onClose: () => void;
}

const BASE_API = config.API_BASE_URL;

const AddBrochureOrder: React.FC<AddBrochureOrderProps> = ({
  fundraiserID,
  brochureOnlyAccountID,
  onClose,
}) => {
  const [searchName, setSearchName] = useState("");
  const [fundraiserParticipants, setFundraiserParticipants] = useState<
    ParticipantInfo[]
  >([]);
  const [selectedParticipant, setSelectedParticipant] =
    useState<ParticipantInfo | null>(null);
  const [matchingParticipants, setMatchingParticipants] = useState<
    ParticipantInfo[]
  >([]);
  const [primaryDivisions, setPrimaryDivisions] = useState<PrimaryDivision[]>(
    []
  );
  const [secondaryDivisions, setSecondaryDivisions] = useState<
    SecondaryDivision[]
  >([]);
  const [primaryDivision, setPrimaryDivision] = useState<number | null>(null);
  const [secondaryDivision, setSecondaryDivision] = useState<number | null>(
    null
  );
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [highlightedProductIndex, setHighlightedProductIndex] = useState<
    number | null
  >(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const [searchSKU, setSearchSKU] = useState("");
  const [quantity, setQuantity] = useState<string>("");
  const [skuQuantityList, setSkuQuantityList] = useState<
    { product: number; quantity: number }[]
  >([]);
  const [isOrderSuccessful, setIsOrderSuccessful] = useState(false);
  const [fadeClass, setFadeClass] = useState("");

  useEffect(() => {
    const fetchDivisionsAndProducts = async () => {
      try {
        const [divisionsResponse, productsResponse, participantResponse] =
          await Promise.all([
            fetchWithAuth(`${BASE_API}division/fundraiser/${fundraiserID}`),
            fetchWithAuth(`${BASE_API}products/fundraiser/${fundraiserID}`),
            fetchWithAuth(
              `${BASE_API}fundraiser/${fundraiserID}/participants/`
            ),
          ]);

        if (!divisionsResponse.ok || !productsResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        const divisionsData: PrimaryDivision[] = await divisionsResponse.json();
        const productsData: Product[] = await productsResponse.json();
        const participantData: ParticipantInfo[] =
          await participantResponse.json();

        setPrimaryDivisions(divisionsData);
        setProducts(productsData);
        setFundraiserParticipants(participantData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setPrimaryDivisions([]);
        setProducts([]);
      }
    };

    fetchDivisionsAndProducts();
  }, [fundraiserID]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setSearchName(name);

    const matches = name
      ? fundraiserParticipants.filter(
          (participant) =>
            participant.account_profile && // Check if account_profile exists
            (
              participant.account_profile.first_name +
              " " +
              participant.account_profile.last_name
            )
              .toLowerCase()
              .includes(name.toLowerCase())
        )
      : [];

    setMatchingParticipants(matches);
    setSelectedParticipant(null);
    setPrimaryDivision(null);
    setSecondaryDivision(null);
    setHighlightedIndex(null);
  };

  const handleParticipantSelect = (participant: ParticipantInfo) => {
    setSelectedParticipant(participant);
    setPrimaryDivision(participant.primary_division.id);
    setSecondaryDivision(participant.secondary_division.id);
    setMatchingParticipants([]);
    setSearchName(
      `${participant.account_profile.first_name} ${participant.account_profile.last_name}`
    );

    const selectedPrimary = primaryDivisions.find(
      (div) => div.id === participant.primary_division.id
    );
    setSecondaryDivisions(
      selectedPrimary ? selectedPrimary.secondary_divisions : []
    );
  };

  const handlePrimaryDivisionChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedPrimaryId = Number(event.target.value);
    setPrimaryDivision(selectedPrimaryId);

    const selectedPrimary = primaryDivisions.find(
      (div) => div.id === selectedPrimaryId
    );
    setSecondaryDivisions(
      selectedPrimary ? selectedPrimary.secondary_divisions : []
    );
  };

  const handleSKUChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const skuSearch = event.target.value;
    setSearchSKU(skuSearch);

    const matches = skuSearch
      ? products.filter((product) =>
          product.sku.toString().startsWith(skuSearch)
        )
      : [];

    setFilteredProducts(matches);
    setHighlightedProductIndex(null);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers and empty string
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setQuantity(value); // Set quantity to the input value
    }
  };

  const handleQuantityKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      event.key === "Enter" &&
      searchSKU &&
      quantity !== "" &&
      Number(quantity) > 0
    ) {
      const selectedSKU = searchSKU.split(" | ")[0];
      setSkuQuantityList((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.product === Number(selectedSKU)
        );
        if (existingIndex > -1) {
          // If SKU already exists, update the quantity
          const updatedList = prev.map((item, index) =>
            index === existingIndex
              ? { ...item, quantity: item.quantity + Number(quantity) }
              : item
          );
          return updatedList;
        } else {
          // If SKU doesn't exist, add a new entry
          return [
            ...prev,
            { product: Number(selectedSKU), quantity: Number(quantity) },
          ];
        }
      });
      setSearchSKU("");
      setQuantity("");
      setFilteredProducts([]);

      // Focus back to the SKU input
      skuInputRef.current?.focus();
    }
  };

  const handleProductSelect = (product: Product) => {
    setSearchSKU(`${product.sku} | ${product.item_display_name}`);
    setFilteredProducts([]);
    setHighlightedProductIndex(null);
    setQuantity("");
    quantityInputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (matchingParticipants.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev === null
            ? 0
            : Math.min(prev + 1, matchingParticipants.length - 1)
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev === null
            ? matchingParticipants.length - 1
            : Math.max(prev - 1, 0)
        );
      } else if (event.key === "Enter" && highlightedIndex !== null) {
        handleParticipantSelect(matchingParticipants[highlightedIndex]);
      }
    }
  };

  const handleProductKeyDown = (event: React.KeyboardEvent) => {
    if (filteredProducts.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedProductIndex((prev) =>
          prev === null ? 0 : Math.min(prev + 1, filteredProducts.length - 1)
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedProductIndex((prev) =>
          prev === null ? filteredProducts.length - 1 : Math.max(prev - 1, 0)
        );
      } else if (event.key === "Enter" && highlightedProductIndex !== null) {
        handleProductSelect(filteredProducts[highlightedProductIndex]);
      }
    }
  };

  const handleSubmit = async () => {
    if (!skuQuantityList.length) {
      alert("Please add at least one product to the order.");
      return;
    }

    let participantId = selectedParticipant?.id;
    // If no participant is selected, create a new one
    if (!participantId) {
      try {
        const newParticipant: ParticipantCreationResponse =
          await createParticipant();
        participantId = newParticipant.participant_profile_id;
      } catch (error) {
        console.error("Error creating participant:", error);
        alert("Failed to create participant. Please try again.");
        return;
      }
    }

    // Now that we have a participant ID, submit the order
    const orderToSubmit: OrderToSubmit = {
      fundraiser_participant: participantId,
      customer_name: `${
        selectedParticipant?.account_profile.first_name || searchName
      }`,
      order_type: "brochure",
      shipping_info: null, // Add your shipping info if needed
      products: skuQuantityList,
    };

    await submitOrder(orderToSubmit);
  };

  const clearForm = () => {
    setSelectedParticipant(null);
    setPrimaryDivision(null);
    setSecondaryDivision(null);
    setSearchName("");
    setSkuQuantityList([]);
  };

  const splitName = (name: string): [string, string] => {
    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
      return [parts[0], ""]; // Single name with an empty last name
    } else {
      return [parts[0], parts.slice(1).join(" ")]; // First part as first name, rest as last name
    }
  };

  const createParticipant = async () => {
    if (primaryDivision === null || secondaryDivision === null) {
      throw new Error("Divisions cannot be null for participant creation");
    }

    const profileName: string[] = splitName(searchName);
    const participantCreationData: CreateFundraiserParticipant = {
      fundraiser_id: fundraiserID,
      first_name: profileName[0],
      last_name: profileName[1],
      account_id: brochureOnlyAccountID,
      primary_division_id: primaryDivision,
      secondary_division_id: secondaryDivision,
    };

    const response = await fetchWithAuth(
      `${BASE_API}fundraiser_roles/participant/add/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(participantCreationData), // Adjust this based on your API's requirements
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create participant");
    }

    return await response.json(); // Assuming the response contains the created participant
  };

  const submitOrder = async (orderToSubmit: OrderToSubmit) => {
    try {
      const response = await fetchWithAuth(`${BASE_API}create_order/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderToSubmit),
      });

      if (!response.ok) {
        throw new Error("Failed to submit order");
      }

      const result = await response.json();
      setIsOrderSuccessful(true);
      setFadeClass("add-brochure-order-success-fade-in");

      setTimeout(() => {
        setFadeClass("add-brochure-order-success-fade-out");
      }, 1500);

      setTimeout(() => {
        setIsOrderSuccessful(false);
      }, 2000);
      clearForm();
    } catch (error) {
      console.error("Error submitting order:", error);
      alert("Failed to submit order. Please try again.");
    }
  };
  const totalOrderPrice = skuQuantityList
    .reduce((sum, item) => {
      const product = products.find((p) => p.sku === item.product);
      const itemCost = parseFloat(product?.item_cost ?? "0"); // Ensure item_cost is treated as a number
      return sum + itemCost * item.quantity;
    }, 0)
    .toFixed(2); // Apply toFixed AFTER summing
  return (
    <div className="add-brochure-order-container" onClick={onClose}>
      <div
        className="add-brochure-order-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {isOrderSuccessful && (
          <div className={`add-brochure-order-success-message ${fadeClass}`}>
            Order successfully created
          </div>
        )}
        <button className="add-brochure-order-close-button" onClick={onClose}>
          &times;
        </button>

        <h2>Order Brochure</h2>

        <label htmlFor="name-search">Participant Name (First and Last):</label>
        <div
          className="participant-name-container"
          style={{ position: "relative" }}
        >
          <input
            id="name-search"
            type="text"
            value={searchName}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder="Type name..."
            autoComplete="off"
          />
          {matchingParticipants.length > 0 && (
            <ul className="add-brochure-order-dropdown">
              {matchingParticipants.map((participant, index) => (
                <li
                  key={participant.account_profile.id}
                  onClick={() => handleParticipantSelect(participant)}
                  className={`add-brochure-order-dropdown-item ${
                    highlightedIndex === index
                      ? "add-brochure-order-highlighted"
                      : ""
                  }`}
                >
                  {participant.account_profile.first_name}{" "}
                  {participant.account_profile.last_name}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="add-brochure-order-division-row">
          <div>
            <label htmlFor="primary-division">Primary Division:</label>
            {selectedParticipant ? (
              <input
                id="primary-division"
                type="text"
                value={
                  primaryDivisions.find((div) => div.id === primaryDivision)
                    ?.division_name || ""
                }
                readOnly
              />
            ) : (
              <select
                id="primary-division"
                value={primaryDivision ?? -1}
                onChange={handlePrimaryDivisionChange}
              >
                <option value="-1">Select a Primary Division</option>
                {primaryDivisions.map((div) => (
                  <option key={div.id} value={div.id}>
                    {div.division_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label htmlFor="secondary-division">Secondary Division:</label>
            <select
              id="secondary-division"
              value={secondaryDivision ?? -1}
              onChange={(e) => setSecondaryDivision(Number(e.target.value))}
              disabled={!secondaryDivisions.length}
            >
              <option value="-1">Select a Secondary Division</option>
              {secondaryDivisions.map((div) => (
                <option key={div.id} value={div.id}>
                  {div.division_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="add-brochure-order-sku-quantity-row">
          <div className="sku-input-container">
            <label htmlFor="sku-search">SKU:</label>
            <input
              id="sku-search"
              type="text"
              value={searchSKU}
              onChange={handleSKUChange}
              onKeyDown={handleProductKeyDown}
              placeholder="Type SKU..."
              autoComplete="off"
              ref={skuInputRef}
            />
            {filteredProducts.length > 0 && (
              <ul className="add-brochure-order-dropdown">
                {filteredProducts.map((product, index) => (
                  <li
                    key={product.sku}
                    onClick={() => handleProductSelect(product)}
                    className={`add-brochure-order-dropdown-item ${
                      highlightedProductIndex === index
                        ? "add-brochure-order-highlighted"
                        : ""
                    }`}
                  >
                    {product.sku} | {product.item_display_name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="quantity-input-container">
            <label htmlFor="quantity">Quantity:</label>
            <input
              id="quantity"
              type="number"
              ref={quantityInputRef}
              value={quantity}
              onChange={handleQuantityChange}
              onKeyDown={handleQuantityKeyDown}
              min="1"
              placeholder="Enter quantity"
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={
            primaryDivision === null ||
            secondaryDivision === null ||
            searchName === "" ||
            skuQuantityList.length === 0
          }
        >
          Submit Order
        </button>

        <div className="add-brochure-order-sku-quantity-list">
          <h3>SKU Quantity List</h3>
          <ul>
            {skuQuantityList.map((item, index) => {
              const product = products.find((p) => p.sku === item.product);
              const totalPrice =
                (typeof product?.item_cost === "number"
                  ? product.item_cost
                  : parseFloat(product?.item_cost ?? "0")) * item.quantity;

              const handleQuantityChange = (
                e: React.ChangeEvent<HTMLInputElement>
              ) => {
                const newQty = parseInt(e.target.value, 10) || 0;
                setSkuQuantityList((prevList) =>
                  prevList.map((skuItem, i) =>
                    i === index ? { ...skuItem, quantity: newQty } : skuItem
                  )
                );
              };

              const handleDelete = () => {
                setSkuQuantityList((prevList) =>
                  prevList.filter((_, i) => i !== index)
                );
              };

              return (
                <li
                  key={index}
                  className="add-brochure-order-sku-quantity-item"
                >
                  <img
                    src={
                      `${config.BACKEND_BASE_IMAGE_LOCATION}${product?.image_url}` ||
                      ""
                    }
                    alt={product?.item_display_name}
                  />
                  <div className="add-brochure-order-sku">{item.product}</div>
                  <div className="add-brochure-order-name">
                    {product?.item_display_name}
                  </div>
                  <div className="add-brochure-order-qty">
                    <label htmlFor={`qty-input-${index}`}>Qty:</label>
                    <input
                      id={`qty-input-${index}`}
                      type="number"
                      value={item.quantity}
                      min="1"
                      onChange={handleQuantityChange}
                    />
                  </div>
                  <div className="add-brochure-order-total">
                    ${totalPrice.toFixed(2)}
                  </div>
                  <div
                    className="add-brochure-order-delete-btn"
                    onClick={handleDelete}
                  >
                    &times;
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Running total */}
          <div className="add-brochure-order-running-total">
            Total: ${totalOrderPrice}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBrochureOrder;
